import { useEffect, useRef } from 'react';
import { useParams, useNavigate, Navigate, Link } from 'react-router';
import { motion } from 'framer-motion';
import { useMatchStore } from '../../../stores/match';
import { parkCurrent } from '../../../stores/matches';
import { useAccountStore } from '../../../stores/account';
import { allHeroes } from '../../../game/data/heroes';
import { WordMark } from '../../../components/WordMark';
import { ScreenPlate } from '../../../components/ScreenPlate';
import { useEffectiveReducedMotion } from '../../../components/useReducedMotion';
import { EDGES } from '../../../theme/tokens';

// R7 — basic results screen. Reached when the human is eliminated
// (HP ≤ 0) or, eventually, when only one player remains. Single-
// player + single-opponent flow means we always render as "you got
// pushed out". Real placements + stats land when the 7-AI tracking
// arrives.

export function ResultsPage() {
  const { matchId: urlMatchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const reducedMotion = useEffectiveReducedMotion();
  const storedMatchId = useMatchStore((s) => s.matchId);
  const heroId = useMatchStore((s) => s.heroId);
  const roundNumber = useMatchStore((s) => s.roundNumber);
  const placement = useMatchStore((s) => s.placement);
  const endMatch = useMatchStore((s) => s.endMatch);
  // Match-summary state — values reflect what the player had when the
  // run ended (board + tier + final HP). Safe to read post-elimination
  // since the eliminate action only sets `placement`, not these.
  const finalBoard = useMatchStore((s) => s.board);
  const finalTavernTier = useMatchStore((s) => s.tavernTier);
  const finalGold = useMatchStore((s) => s.gold);
  const finalHealth = useMatchStore((s) => s.health);

  if (!urlMatchId || storedMatchId !== urlMatchId || !heroId) {
    return <Navigate to="/play" replace />;
  }

  const hero = allHeroes.find((h) => h.id === heroId);
  const won = placement === 1;

  // Record the match result in the account store exactly once per
  // mount. Guarded by a ref so React-strict-mode double-mount doesn't
  // double-count, and so navigating back here from Menu doesn't re-record.
  const recordMatchResult = useAccountStore((s) => s.recordMatchResult);
  const recordedRef = useRef(false);
  useEffect(() => {
    if (recordedRef.current) return;
    if (heroId && placement !== null) {
      recordMatchResult(heroId, placement);
      recordedRef.current = true;
    }
  }, [heroId, placement, recordMatchResult]);
  const placementOrdinal = placement
    ? placement === 1
      ? '1st'
      : placement === 2
        ? '2nd'
        : placement === 3
          ? '3rd'
          : `${placement}th`
    : null;

  // Park BEFORE clearing. endMatch() nulls matchId, and parkCurrent() bails on
  // a null matchId, so clearing first meant the sidecar never learned the match
  // had ended — the row stayed 'in-progress' and kept eating one of the
  // MATCHES_CAP slots forever. Order matters here; see reconcileFinished().
  function finishAndLeave(to: string) {
    parkCurrent();
    endMatch();
    navigate(to);
  }

  function playAgain() {
    finishAndLeave('/play');
  }

  function backToMenu() {
    finishAndLeave('/menu');
  }

  return (
    <main className="pt-safe relative h-dvh w-full overflow-hidden bg-asphalt">
      {/* The payoff beat gets a real picture instead of a tinted gradient, and it
          branches: a hopped-up lowrider in golden-hour confetti for a win, an empty
          alley at grey pre-dawn for a loss. The loss plate is deliberately quiet and
          deflated rather than bleak — losing 8th should sting, not punish. */}
      <ScreenPlate
        src={won ? 'plates/results_win.webp' : 'plates/results_loss.webp'}
        scrim={won ? 0.62 : 0.7}
      />

      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background: won
            ? 'radial-gradient(ellipse at center, rgba(95,209,126,0.22) 0%, rgba(95,209,126,0) 60%)'
            : 'radial-gradient(ellipse at center, rgba(230,52,42,0.20) 0%, rgba(230,52,42,0) 60%)',
        }}
      />

      <section className="relative mx-auto flex h-full w-full max-w-5xl flex-col items-center justify-center gap-md overflow-y-auto px-md py-sm md:max-w-4xl md:gap-lg md:px-lg md:py-md lg:max-w-5xl lg:gap-xl lg:py-lg">
        <span className="md:hidden">
          <WordMark size="sm" />
        </span>
        <span className="hidden md:inline lg:hidden">
          <WordMark size="md" />
        </span>
        <span className="hidden lg:inline">
          <WordMark size="lg" />
        </span>

        <div className="text-center">
          <p className="font-data text-[10px] uppercase tracking-[0.3em] text-marker md:text-[12px] lg:text-[14px]">
            run · {won ? 'won' : 'ended'}
            {placementOrdinal && <> · <span className="text-bone">{placementOrdinal}</span></>}
          </p>
          <h1
            className={`mt-sm font-cholo text-[26px] leading-none sm:text-[36px] md:text-[60px] lg:text-[100px] ${
              won ? 'text-glowStick' : 'text-aerosolRed'
            }`}
          >
            {won ? 'Wall held.' : 'Pushed out'}
          </h1>
          <p className="mt-xs hidden text-[15px] text-marker md:block md:text-[18px] lg:text-[22px]">
            {won ? (
              <>
                {hero?.name} stood alone on round{' '}
                <span className="text-bone">{roundNumber}</span>. The block is
                yours tonight.
              </>
            ) : (
              <>
                {hero?.name} fell on round{' '}
                <span className="text-bone">{roundNumber}</span>. The wall isn't
                yours tonight.
              </>
            )}
          </p>
        </div>

        {/* Match summary — what the run actually was. Shown as a quick
            stat strip so players can read "I went to tier 4 in 8 rounds
            with 12 gold left over" without leaving Results. */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.4 }}
          className="grid w-full max-w-2xl grid-cols-2 gap-sm rounded-md border bg-stucco1 p-sm sm:grid-cols-4 sm:gap-xs md:max-w-2xl md:gap-md md:p-lg"
          style={{ borderColor: EDGES.soft }}
        >
          <SummaryStat label="Rounds" value={String(roundNumber)} />
          <SummaryStat label="Tavern tier" value={String(finalTavernTier)} accent="tecate" />
          <SummaryStat
            label="HP left"
            value={String(Math.max(0, finalHealth))}
            accent={finalHealth > 0 ? 'glowStick' : 'aerosolRed'}
          />
          <SummaryStat label="Gold left" value={String(finalGold)} accent="sodium" />
        </motion.section>

        {/* Final lineup — shows what board the player ended with.
            Renders even on loss; the snapshot is the story of the run. */}
        {finalBoard.some(Boolean) && (
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.4 }}
            className="w-full max-w-md md:max-w-3xl"
          >
            <p className="mb-sm text-center font-data text-[10px] uppercase tracking-[0.3em] text-marker md:text-[12px]">
              final lineup
            </p>
            <div className="flex flex-wrap justify-center gap-xs md:gap-sm">
              {finalBoard.filter((c): c is NonNullable<typeof c> => !!c).map((unit) => (
                <span
                  key={unit.instanceId}
                  className="inline-flex items-center gap-xs rounded-sm border bg-stucco2 px-sm py-[4px] font-data text-[10px] text-bone md:text-[11px]"
                  style={{ borderColor: 'rgba(245,236,214,0.1)' }}
                  title={unit.name}
                >
                  <span className="truncate max-w-[120px]">{unit.name}</span>
                  <span className="text-aerosolRed">{unit.attack}</span>
                  <span className="text-graphite">/</span>
                  <span className="text-glowStick">{unit.health}</span>
                </span>
              ))}
            </div>
          </motion.section>
        )}

        <div className="flex flex-col items-center gap-md md:flex-row md:gap-lg">
          <motion.button
            onClick={playAgain}
            whileHover={reducedMotion ? undefined : { scale: 1.02 }}
            whileTap={reducedMotion ? undefined : { scale: 0.97 }}
            className="inline-block min-h-[52px] rounded-sm bg-sodium px-xl py-md font-data text-[16px] uppercase tracking-[0.2em] text-sharpie shadow-sodium hover:bg-sodiumGlow lg:min-h-[64px] lg:px-2xl lg:text-[20px]"
          >
            Run it back ›
          </motion.button>
          <Link
            to="/menu"
            onClick={(e) => {
              e.preventDefault();
              backToMenu();
            }}
            className="inline-flex min-h-[44px] items-center rounded-sm bg-stucco2 px-lg py-sm font-data text-[14px] uppercase tracking-[0.2em] text-bone transition-colors hover:bg-stucco3 lg:min-h-[52px] lg:text-[16px]"
          >
            ← menu
          </Link>
          <Link
            to="/profile"
            className="font-data text-[10px] uppercase tracking-[0.3em] text-graphite hover:text-marker md:text-[12px]"
          >
            view profile →
          </Link>
        </div>
      </section>
    </main>
  );
}

interface SummaryStatProps {
  label: string;
  value: string;
  accent?: 'sodium' | 'glowStick' | 'aerosolRed' | 'tecate';
}

function SummaryStat({ label, value, accent }: SummaryStatProps) {
  const accentClass =
    accent === 'sodium'
      ? 'text-sodium'
      : accent === 'glowStick'
        ? 'text-glowStick'
        : accent === 'aerosolRed'
          ? 'text-aerosolRed'
          : accent === 'tecate'
            ? 'text-tecate'
            : 'text-bone';
  return (
    <div className="flex flex-col items-start gap-[2px]">
      <p className="font-data text-[9px] uppercase tracking-[0.2em] text-graphite md:text-[10px]">
        {label}
      </p>
      <p className={`font-data text-[18px] font-bold leading-none sm:text-[20px] md:text-[24px] lg:text-[28px] ${accentClass}`}>
        {value}
      </p>
    </div>
  );
}
